import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface SecretaryOrganisationAssignment {
    secretaryId: string;
    organisationId: string;
    isDefault: boolean;
}

/** A clinician's secretary assignments, scoped per organisation. */
export async function selectSecretaryOrganisations(
    ownerClinicianId: string
): Promise<SecretaryOrganisationAssignment[]> {
    const { data, error } = await supabaseClient
        .from("secretary_organisation_assignments")
        .select("secretary_id, organisation_id, is_default")
        .eq("owner_clinician_id", ownerClinicianId);

    if (error) throw error;

    return (data ?? []).map((row) => ({
        secretaryId: row.secretary_id,
        organisationId: row.organisation_id,
        isDefault: row.is_default,
    }));
}

export const selectSecretaryOrganisationsQueryOptions = (ownerClinicianId: string) =>
    queryOptions({
        queryKey: ["secretary-organisations", ownerClinicianId],
        queryFn: () => selectSecretaryOrganisations(ownerClinicianId),
        enabled: Boolean(ownerClinicianId),
    });

export function useSelectSecretaryOrganisations(ownerClinicianId: string) {
    return useQuery(selectSecretaryOrganisationsQueryOptions(ownerClinicianId));
}

/** Is this secretary the logged-in clinician's default - for one organisation, or for any shared organisation
 * when organisationId is omitted (used by flat lists like Favourites/Invited that don't carry a single org). */
export function isDefaultSecretaryForOrg(
    assignments: SecretaryOrganisationAssignment[],
    secretaryId: string,
    organisationId?: string
): boolean {
    return assignments.some(
        (a) => a.secretaryId === secretaryId && a.isDefault && (!organisationId || a.organisationId === organisationId)
    );
}

/**
 * Number of clinicians a secretary is linked to. >1 means the secretary is shared, not private to one clinician.
 * Goes through an RPC rather than a plain count query: secretary_owners RLS only lets a clinician see their OWN
 * ownership row, so a direct count would always read back at most 1 regardless of other owners.
 */
export async function selectSecretaryOwnerCount(secretaryId: string): Promise<number> {
    const { data, error } = await supabaseClient.rpc("secretary_owner_count", { p_secretary_id: secretaryId });

    if (error) throw error;
    return data ?? 0;
}

export const secretaryOwnerCountQueryOptions = (secretaryId: string) =>
    queryOptions({
        queryKey: ["secretary-owner-count", secretaryId],
        queryFn: () => selectSecretaryOwnerCount(secretaryId),
        enabled: Boolean(secretaryId),
    });

/** Single source of truth for "is this secretary shared with another clinician?" - used by both the
 * team list row icon and the member dialog, so they never disagree on whether editing is allowed. */
export function useSecretaryOwnerCount(secretaryId: string) {
    return useQuery(secretaryOwnerCountQueryOptions(secretaryId));
}

/** The clinician's default secretary for one organisation, if any (used to pre-fill template/invite contact pickers). */
export async function selectDefaultSecretaryForOrganisation(
    ownerClinicianId: string,
    organisationId: string
): Promise<string | null> {
    const { data, error } = await supabaseClient
        .from("secretary_organisation_assignments")
        .select("secretary_id")
        .eq("owner_clinician_id", ownerClinicianId)
        .eq("organisation_id", organisationId)
        .eq("is_default", true)
        .maybeSingle();

    if (error) throw error;
    return data?.secretary_id ?? null;
}

export const defaultSecretaryForOrganisationQueryOptions = (ownerClinicianId: string, organisationId: string) =>
    queryOptions({
        queryKey: ["default-secretary-for-organisation", ownerClinicianId, organisationId],
        queryFn: () => selectDefaultSecretaryForOrganisation(ownerClinicianId, organisationId),
        enabled: Boolean(ownerClinicianId) && Boolean(organisationId),
    });

export function useDefaultSecretaryForOrganisation(ownerClinicianId: string, organisationId: string) {
    return useQuery(defaultSecretaryForOrganisationQueryOptions(ownerClinicianId, organisationId));
}
